import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Activity } from "lucide-react";

const Row = ({ id, c }) => {
  const Icon = c.ok ? CheckCircle2 : c.required === false ? AlertTriangle : XCircle;
  const tone = c.ok ? "text-emerald-400" : c.required === false ? "text-amber-300" : "text-red-400";
  return (
    <li className="flex items-start gap-2 py-2 border-b border-white/5 last:border-0" data-testid={`health-row-${id}`}>
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${tone}`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-200">{c.label}{c.required === false && !c.ok && <span className="text-[10px] text-amber-300/80 uppercase ml-2">optional</span>}</p>
        <p className="text-[11px] text-gray-500" data-testid={`health-detail-${id}`}>{c.detail}</p>
        {!c.ok && c.fix && <p className="text-[11px] text-gray-400 mt-0.5">Fix: {c.fix}</p>}
      </div>
    </li>
  );
};

export const SourceHealthCard = ({ userId }) => {
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(true);
  const load = useCallback(async (force = false) => {
    setBusy(true);
    try { const { data } = await axios.get(`${API}/prospecting/health/${userId}`, { params: force ? { force: true } : {} }); setHealth(data); }
    catch { setHealth({ ok: false, checks: {}, blocking: ["Health check failed"], error: true }); }
    finally { setBusy(false); }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const down = health?.blocking?.length || 0;
  const headline = !health ? "Checking data sources…" : health.error ? "Could not reach the health check" : down ? `${down} required source${down > 1 ? "s" : ""} down — hunts will be blocked` : "All systems go — hunts can run";
  const tone = !health || busy ? "border-white/10 bg-white/[0.03]" : down || health.error ? "border-red-500/30 bg-red-500/[0.06]" : "border-emerald-500/30 bg-emerald-500/[0.06]";

  return (
    <div className={`rounded-xl border p-3 ${tone}`} data-testid="source-health-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {busy ? <Loader2 className="h-4 w-4 animate-spin text-ism-teal shrink-0" /> : <Activity className={`h-4 w-4 shrink-0 ${down || health?.error ? "text-red-400" : "text-emerald-400"}`} />}
          <div className="min-w-0">
            <p className="text-sm text-white leading-snug" data-testid="source-health-headline">{headline}</p>
            {health?.checkedAt && <p className="text-[11px] text-gray-500">Live check · {new Date(health.checkedAt).toLocaleTimeString()}</p>}
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => load(true)} disabled={busy} className="h-8 border-white/10 text-gray-300 shrink-0" data-testid="source-health-refresh">
          <RefreshCw className={`h-3 w-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Check now
        </Button>
      </div>
      {health && !health.error && (
        <ul className="mt-2" data-testid="source-health-list">{Object.entries(health.checks).map(([id, c]) => <Row key={id} id={id} c={c} />)}</ul>
      )}
    </div>
  );
};
